import { useNavigate, useLocation } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  ChangeEvent,
  useRef,
} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Icon from '../../../components/common/Icon/Icon';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { Button } from '../../../components/ui/Button/Button';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetFleet from '../../../API/hooks/useGetFleetDetails';
import useGetAudioAnnouncementFiles from '../../../API/hooks/announcement/useGetAudioAnnouncementFiles';
import useGetAudioAnnouncementDevices from '../../../API/hooks/announcement/useGetAudioAnnouncementDevices';
import useSendAudioToDevices from '../../../API/hooks/announcement/useSendAudioToDevices';
import useAbortAudio from '../../../API/hooks/announcement/useAbortAudio';
import AssignAnnouncementAudioCard from './AssignAnnouncementAudioCard/AssignAnnouncementAudioCard';
import S, { AssignAnnouncementDatePickerGlobal } from './AssignAnnouncementPageStyles';
import {
  getDefaultTimeoutDate,
  getDeviceDisplayId,
  getMinDateTime,
  getStartOfToday,
} from './assignAnnouncementUtils';
import type { AudioAnnouncementDevice } from '../../../API/types/AudioAnnouncement';
import { toast } from '../../../utils/toast';
import { formatSizeBytes } from '../../../utils/unitsUtils';
import closeIcon from '../Announcement/images/close-icon.svg';

const ANNOUNCEMENT_PATH = '/announcements';
const SIZE_PLACEHOLDER = '—';

type LocationState = { audioId?: string } | undefined;

function AssignAnnouncementPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const fleetId = useGetFleetId();
  const { data: apiFiles = [] } = useGetAudioAnnouncementFiles(fleetId ?? '');
  const { data: fleetData } = useGetFleet(fleetId ?? '');
  const fleetName = fleetData?.name ?? '';

  const audioList = useMemo(
    () => apiFiles.map((f) => ({
      id: f._id,
      name: f.fileName,
      durationSec: f.durationSec ?? null,
      sizeFormatted: f.sizeBytes != null ? formatSizeBytes(f.sizeBytes) : SIZE_PLACEHOLDER,
      audioUrl: f.url,
    })),
    [apiFiles],
  );

  const [initialAudioIdSet, setInitialAudioIdSet] = useState(false);
  const [selectedAudioIdState, setSelectedAudioIdState] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [deselectedDeviceIds, setDeselectedDeviceIds] = useState<string[]>([]);
  const [timeoutDate, setTimeoutDate] = useState<Date>(getDefaultTimeoutDate);
  const datePickerRef = useRef<DatePicker>(null);

  const initialSelectedId = useMemo((): string | null => {
    const fromState = (location.state as LocationState)?.audioId ?? null;
    if (fromState && audioList.some((a) => a.id === fromState)) return fromState;
    return audioList.length > 0 ? audioList[0].id : null;
  }, [audioList, location.state]);

  const selectedAudioIdResolved = selectedAudioIdState ?? initialSelectedId;

  const { data: devicesData } = useGetAudioAnnouncementDevices(fleetId, selectedAudioIdResolved);

  useEffect(() => {
    if (audioList.length === 0 || initialAudioIdSet) return;
    setSelectedAudioIdState(initialSelectedId);
    setInitialAudioIdSet(true);
  }, [audioList, initialAudioIdSet, initialSelectedId]);

  const queuedDevices = useMemo(
    (): AudioAnnouncementDevice[] => (devicesData?.queuedDevices ?? []).filter((d): d is AudioAnnouncementDevice => d != null),
    [devicesData?.queuedDevices],
  );

  const selectedDevices = useMemo(
    (): AudioAnnouncementDevice[] => (devicesData?.selectedDevices ?? []).filter((d): d is AudioAnnouncementDevice => d != null),
    [devicesData?.selectedDevices],
  );

  const filteredSelected = useMemo((): AudioAnnouncementDevice[] => {
    const q = (deviceSearch ?? '').trim().toLowerCase();
    if (!q) return selectedDevices;
    return selectedDevices.filter(
      (d) => getDeviceDisplayId(d).toLowerCase().includes(q),
    );
  }, [selectedDevices, deviceSearch]);

  const filteredQueued = useMemo((): AudioAnnouncementDevice[] => {
    const q = (deviceSearch ?? '').trim().toLowerCase();
    if (!q) return queuedDevices;
    return queuedDevices.filter(
      (d) => getDeviceDisplayId(d).toLowerCase().includes(q),
    );
  }, [queuedDevices, deviceSearch]);

  const devicesToSend = useMemo(
    (): AudioAnnouncementDevice[] => selectedDevices.filter(
      (d) => !deselectedDeviceIds.includes(d.deviceId ?? d._id),
    ),
    [selectedDevices, deselectedDeviceIds],
  );

  useEffect(() => {
    setDeselectedDeviceIds([]);
  }, [selectedAudioIdResolved]);

  const sendMutation = useSendAudioToDevices(
    (res) => {
      const okCount = res.ok?.length ?? 0;
      const failedCount = res.failed?.length ?? 0;
      if (failedCount > 0) {
        toast.error(`${okCount} sent. ${failedCount} failed.`);
      } else {
        toast.success(okCount > 0 ? `Sent to ${okCount} device(s).` : 'Done.');
      }
      navigate(ANNOUNCEMENT_PATH);
    },
    (err) => toast.error(err ?? 'Send failed'),
  );

  const abortMutation = useAbortAudio(
    (res) => {
      const aborted = res.abortedDevices?.length ?? 0;
      const notInQueued = res.notInQueuedState?.length ?? 0;
      const notFound = res.notFoundDevices?.length ?? 0;
      const gatewayFailed = res.gatewayFailed?.length ?? 0;
      const failed = notInQueued + notFound + gatewayFailed;
      if (failed > 0) {
        toast.error(aborted > 0 ? `Aborted ${aborted} device(s). ${failed} could not be aborted.` : 'Some devices could not be aborted.');
      } else {
        toast.success(aborted > 0 ? `Aborted ${aborted} device(s).` : 'Done.');
      }
    },
    (err) => toast.error(err ?? 'Abort failed'),
  );

  const handleGoBack = useCallback((): void => {
    navigate(ANNOUNCEMENT_PATH);
  }, [navigate]);

  const handleSelectAudio = useCallback((id: string): void => {
    setSelectedAudioIdState(id);
  }, []);

  const handlePlayPause = useCallback((id: string, isPlaying: boolean): void => {
    setPlayingId((prev) => {
      if (isPlaying) return id;
      return prev === id ? null : prev;
    });
  }, []);

  const handleRemoveAllSelected = useCallback((): void => {
    setDeselectedDeviceIds(selectedDevices.map((d) => d.deviceId ?? d._id).filter(Boolean));
  }, [selectedDevices]);

  const handleAbortAllQueued = useCallback((): void => {
    if (!fleetId || !selectedAudioIdResolved || queuedDevices.length === 0) return;
    const devices = queuedDevices
      .filter((d) => d.deviceId != null && d.deviceId !== '')
      .map((d) => ({ deviceId: d.deviceId as string }));
    if (devices.length === 0) return;
    abortMutation.mutate({ fleetId, audioId: selectedAudioIdResolved, devices });
  }, [fleetId, selectedAudioIdResolved, queuedDevices, abortMutation]);

  const handleDeselectDevice = useCallback((device: AudioAnnouncementDevice): void => {
    const id = device.deviceId ?? device._id;
    if (id) setDeselectedDeviceIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleReselectDevice = useCallback((device: AudioAnnouncementDevice): void => {
    const id = device.deviceId ?? device._id;
    if (id) setDeselectedDeviceIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const handleRemoveQueuedDevice = useCallback((device: AudioAnnouncementDevice): void => {
    if (!fleetId || !selectedAudioIdResolved) return;
    const deviceId = device.deviceId ?? device._id;
    if (deviceId == null || deviceId === '') return;
    abortMutation.mutate({
      fleetId,
      audioId: selectedAudioIdResolved,
      devices: [{ deviceId }],
    });
  }, [fleetId, selectedAudioIdResolved, abortMutation]);

  const handleSend = useCallback((): void => {
    if (!fleetId || !selectedAudioIdResolved || devicesToSend.length === 0) return;
    const devices = devicesToSend
      .filter((d) => d.deviceId != null && d.deviceId !== '')
      .map((d) => ({ deviceId: d.deviceId as string }));
    if (devices.length === 0) return;
    const timestampSecEpochToTimeout = Math.floor(timeoutDate.getTime() / 1000);
    sendMutation.mutate({
      fleetId,
      audioId: selectedAudioIdResolved,
      devices,
      timestampSecEpochToTimeout,
    });
  }, [fleetId, selectedAudioIdResolved, devicesToSend, timeoutDate, sendMutation]);

  const [minDate, setMinDate] = useState(() => getStartOfToday());
  const [minTimeForToday, setMinTimeForToday] = useState(() => getMinDateTime());

  useEffect(() => {
    const id = setInterval(() => {
      setMinDate(getStartOfToday());
      setMinTimeForToday(getMinDateTime());
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setTimeoutDate((prev) => {
      if (prev.getTime() < minTimeForToday.getTime()) {
        return new Date(minTimeForToday);
      }
      return prev;
    });
  }, [minTimeForToday]);

  const isSelectedDateToday = useMemo((): boolean => {
    const today = getStartOfToday();
    const selected = new Date(timeoutDate);
    selected.setHours(0, 0, 0, 0);
    return selected.getTime() === today.getTime();
  }, [timeoutDate]);

  const filterTime = useCallback(
    (time: Date): boolean => {
      if (!isSelectedDateToday) return true;
      return time.getTime() >= minTimeForToday.getTime();
    },
    [isSelectedDateToday, minTimeForToday],
  );

  const sendDisabled = !selectedAudioIdResolved || devicesToSend.length === 0 || sendMutation.isLoading;

  return (
    <S.Wrapper>
      <AssignAnnouncementDatePickerGlobal />
      <S.HeaderRow>
        <S.PreviewContainer>
          <Button height="44px" padding="10px 12px" onClick={handleGoBack} aria-label="Back to Announcement">
            <Icon iconKey={IconKeys.ARROW_LEFT} />
          </Button>
          <S.TitleBlock>
            <S.Title>Assign Announcement</S.Title>
            <S.Subtitle>Send Announcement to {fleetName || 'fleet'}</S.Subtitle>
          </S.TitleBlock>
        </S.PreviewContainer>
      </S.HeaderRow>
      <S.Content>
        <S.AudioFilesSection>
          <S.SectionTitle>Audio Files</S.SectionTitle>
          <S.AudioCardList>
            {audioList.map((item) => (
              <AssignAnnouncementAudioCard
                key={item.id}
                id={item.id}
                name={item.name}
                durationSec={item.durationSec}
                sizeFormatted={item.sizeFormatted}
                audioUrl={item.audioUrl}
                isSelected={selectedAudioIdResolved === item.id}
                onSelect={handleSelectAudio}
                playingId={playingId}
                onPlayPause={handlePlayPause}
              />
            ))}
          </S.AudioCardList>
        </S.AudioFilesSection>

        <S.TimeoutSection aria-labelledby="timeout-label">
          <S.TimeoutLabel id="timeout-label" htmlFor="announcement-timeout">Select Timeout</S.TimeoutLabel>
          <S.DatePickerWrap className="assign-announcement-timeout">
            <S.DatePickerInputWrap>
              <DatePicker
                ref={datePickerRef}
                id="announcement-timeout"
                selected={timeoutDate}
                onChange={(date: Date | null): void => {
                  if (!date) return;
                  const today = getStartOfToday();
                  const chosenDay = new Date(date);
                  chosenDay.setHours(0, 0, 0, 0);
                  if (chosenDay.getTime() === today.getTime() && date.getTime() < minTimeForToday.getTime()) {
                    setTimeoutDate(new Date(minTimeForToday));
                  } else {
                    setTimeoutDate(date);
                  }
                }}
                minDate={minDate}
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="MMM d, yyyy h:mm aa"
                filterTime={filterTime}
                popperClassName="assign-announcement-timeout-popper"
                popperPlacement="bottom-start"
              />
              <S.DatePickerIconWrap
                type="button"
                onClick={() => datePickerRef.current?.setFocus()}
                aria-label="Open Calendar"
              >
                <Icon iconKey={IconKeys.DATE} />
              </S.DatePickerIconWrap>
            </S.DatePickerInputWrap>
          </S.DatePickerWrap>
        </S.TimeoutSection>

        <S.DevicesSection>
          <S.SectionTitle>Select Devices</S.SectionTitle>
          <S.SearchWrap>
            <S.SearchInputWrap>
              <S.SearchInputField
                type="text"
                name="deviceSearch"
                placeholder="Search Devices"
                value={deviceSearch}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => setDeviceSearch(e?.target?.value ?? '')}
                aria-label="Search Devices"
                autoComplete="off"
              />
              <S.SearchIconWrap aria-hidden>
                <Icon iconKey={IconKeys.SEARCH} />
              </S.SearchIconWrap>
            </S.SearchInputWrap>
          </S.SearchWrap>
        </S.DevicesSection>

        <S.DevicesSection>
          <S.SectionHeader>
            <S.SectionTitleWithCount>
              Selected Devices ({devicesToSend.length}/{selectedDevices.length})
            </S.SectionTitleWithCount>
            {selectedDevices.length > 0 && (
              <S.LinkButton type="button" $variant="selected" onClick={handleRemoveAllSelected} aria-label="Remove all">
                Remove all
              </S.LinkButton>
            )}
          </S.SectionHeader>
          <S.DeviceTagList>
            {filteredSelected.length === 0 ? (
              <S.EmptyMessage>No selected devices</S.EmptyMessage>
            ) : (
              filteredSelected.map((device) => {
                const id = device._id ?? device.deviceId;
                if (id == null) return null;
                const displayId = getDeviceDisplayId(device);
                const deviceIdForSend = device.deviceId ?? id;
                const isSelectedForSend = !deselectedDeviceIds.includes(deviceIdForSend);
                return (
                  <S.DeviceTag
                    key={id}
                    $variant="selected"
                    $isSelectedForSend={isSelectedForSend}
                    {...(!isSelectedForSend && {
                      role: 'button',
                      tabIndex: 0,
                      onClick: (): void => handleReselectDevice(device),
                      onKeyDown: (e: { key: string; preventDefault: () => void }): void => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleReselectDevice(device);
                        }
                      },
                    })}
                  >
                    <S.DeviceTagLabel title={displayId}>{displayId}</S.DeviceTagLabel>
                    <S.DeviceTagRemove
                      type="button"
                      onClick={(e): void => {
                        e.stopPropagation();
                        handleDeselectDevice(device);
                      }}
                      aria-label={`Deselect ${displayId}`}
                    >
                      <img src={closeIcon} alt="Close Icon" />
                    </S.DeviceTagRemove>
                  </S.DeviceTag>
                );
              })
            )}
          </S.DeviceTagList>
        </S.DevicesSection>

        <S.DevicesSection>
          <S.SectionHeader>
            <S.SectionTitleWithCount>Queued Devices ({filteredQueued.length})</S.SectionTitleWithCount>
            {queuedDevices.length > 0 && (
              <S.LinkButton
                $variant="queued"
                type="button"
                onClick={handleAbortAllQueued}
                disabled={abortMutation.isLoading}
                aria-label="Abort all"
              >
                Abort All
              </S.LinkButton>
            )}
          </S.SectionHeader>
          <S.DeviceTagList>
            {filteredQueued.length === 0 ? (
              <S.EmptyMessage>No queued devices</S.EmptyMessage>
            ) : (
              filteredQueued.map((device) => {
                const id = device._id ?? device.deviceId;
                if (id == null) return null;
                const displayId = getDeviceDisplayId(device);
                return (
                  <S.DeviceTag key={id} $variant="queued">
                    <S.DeviceTagLabel title={displayId}>{displayId}</S.DeviceTagLabel>
                    <S.DeviceTagRemove
                      type="button"
                      onClick={(): void => handleRemoveQueuedDevice(device)}
                      disabled={abortMutation.isLoading}
                      aria-label={`Abort ${displayId}`}
                    >
                      <img src={closeIcon} alt="Close Icon" />
                    </S.DeviceTagRemove>
                  </S.DeviceTag>
                );
              })
            )}
          </S.DeviceTagList>
        </S.DevicesSection>

        <S.SendButtonWrap>
          <Button
            height="40px"
            padding="10px 20px"
            onClick={handleSend}
            disabled={sendDisabled}
            aria-label="Send Now"
          >
            Send Now
          </Button>
        </S.SendButtonWrap>
      </S.Content>
    </S.Wrapper>
  );
}

export default AssignAnnouncementPage;
